import * as React from "react";
import { Link } from "react-router-dom";
import { Breadcrumb } from "@/components/ui/breadcrumb";
import { Table, TableWrapper } from "@/components/ui/table";
import { InfoBanner } from "@/components/ui/banner";
import { StatCard } from "@/components/data-display/stat-card";
import { toast } from "@/components/ui/toast";
import { localOnly } from "@/lib/fixture-notice";
import { cn } from "@/lib/utils";
import {
  ACTIVE_ALERT_STATUSES,
  ALERTS,
  ALERT_DETAIL,
  BROADCAST_ROWS,
  BROADCAST_STATS,
  CHAINS,
  FRESHNESS_ROWS,
  MONITOR_PIPELINE_BAR,
  MONITOR_STATS,
  PIPELINE_BAR,
  PROVIDERS,
  type AlertStatus,
  type OversightTone,
} from "./oversight-data";
import { EventTimeline, Panel, SectionTitle, SegmentedBar, ToneBadge, severityTone, statusToneFor } from "./oversight-ui";

function HealthLegend({ segments }: { segments: { label: string; percent?: number; flex?: number; tone: OversightTone }[] }) {
  return (
    <ul className="mt-3 flex flex-wrap gap-x-5 gap-y-1.5">
      {segments.map((s) => (
        <li key={s.label} className="flex items-center gap-2 text-xs text-muted">
          <ToneBadge tone={s.tone} dot={true}>{s.label}</ToneBadge>
          {s.percent !== undefined && <span className="tabular-nums">{s.percent}%</span>}
        </li>
      ))}
    </ul>
  );
}

/* MON-00 · Monitoring Home */
export function MonitoringOverview() {
  const active = ALERTS.filter((a) => ACTIVE_ALERT_STATUSES.includes(a.status));

  return (
    <div>
      <div className="mb-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
        {MONITOR_STATS.map((s) => (
          <StatCard key={s.label} value={s.value} label={s.label} hint={s.hint} />
        ))}
      </div>

      <div className="mb-6">
        <SegmentedBar segments={MONITOR_PIPELINE_BAR} height={10} />
        <HealthLegend segments={MONITOR_PIPELINE_BAR} />
      </div>

      <Panel title="Active Alerts" bodyClassName="p-0">
        <TableWrapper className="rounded-none border-0">
          <Table className="dense">
            <thead>
              <tr className="border-b border-line bg-bg text-left text-xs font-semibold text-muted">
                <th className="px-4 py-2.5">ID</th>
                <th className="px-4 py-2.5">Title</th>
                <th className="px-4 py-2.5">Severity</th>
                <th className="px-4 py-2.5">Owner</th>
                <th className="px-4 py-2.5">Status</th>
              </tr>
            </thead>
            <tbody>
              {active.map((a) => (
                <tr key={a.id} className="border-b border-line last:border-b-0">
                  <td className="px-4 py-2.5">
                    <Link to="/monitoring/alerts" className="font-mono text-[13px] font-medium text-primary hover:underline">
                      {a.id}
                    </Link>
                  </td>
                  <td className="px-4 py-2.5 text-[13px] font-medium text-ink">{a.title}</td>
                  <td className="px-4 py-2.5"><ToneBadge tone={severityTone(a.severity)}>{a.severity}</ToneBadge></td>
                  <td className="px-4 py-2.5 text-[13px] text-text">{a.owner}</td>
                  <td className="px-4 py-2.5"><ToneBadge tone={statusToneFor(a.status)}>{a.status}</ToneBadge></td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableWrapper>
      </Panel>

      <div className="grid gap-5 lg:grid-cols-2">
        <Panel title="Chain Health" bodyClassName="p-0">
          <TableWrapper className="rounded-none border-0">
            <Table className="dense">
              <thead>
                <tr className="border-b border-line bg-bg text-left text-xs font-semibold text-muted">
                  <th className="px-4 py-2.5">Chain</th>
                  <th className="px-4 py-2.5">Status</th>
                  <th className="px-4 py-2.5">Block Height</th>
                </tr>
              </thead>
              <tbody>
                {CHAINS.map((c) => (
                  <tr key={c.id} className="border-b border-line last:border-b-0">
                    <td className="px-4 py-2.5 text-[13px] font-medium text-ink">{c.name}</td>
                    <td className="px-4 py-2.5"><ToneBadge tone={c.statusTone}>{c.statusLabel}</ToneBadge></td>
                    <td className="px-4 py-2.5 font-mono text-[13px] tabular-nums text-text">{c.blockHeight}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrapper>
        </Panel>

        <Panel title="Provider Health" bodyClassName="p-0">
          <TableWrapper className="rounded-none border-0">
            <Table className="dense">
              <thead>
                <tr className="border-b border-line bg-bg text-left text-xs font-semibold text-muted">
                  <th className="px-4 py-2.5">Provider</th>
                  <th className="px-4 py-2.5">Status</th>
                  <th className="px-4 py-2.5">Latency p50</th>
                </tr>
              </thead>
              <tbody>
                {PROVIDERS.map((p) => (
                  <tr key={p.id} className="border-b border-line last:border-b-0">
                    <td className="px-4 py-2.5 text-[13px] font-medium text-ink">{p.name}</td>
                    <td className="px-4 py-2.5"><ToneBadge tone={p.statusTone}>{p.statusLabel}</ToneBadge></td>
                    <td className="px-4 py-2.5 font-mono text-[13px] tabular-nums text-text">{p.latencyP50}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrapper>
        </Panel>
      </div>
    </div>
  );
}

/* MON-02 · Provider Health */
export function ProviderHealthDetail() {
  const provider = PROVIDERS[0];

  return (
    <div>
      <Breadcrumb items={[{ label: "Monitoring", href: "/monitoring" }, { label: provider.name }]} />

      <div className="mb-6 grid grid-cols-2 gap-5 rounded-lg border border-line bg-surface px-5 py-4 sm:grid-cols-3">
        <div>
          <div className="text-xs text-muted">Provider</div>
          <div className="mt-1 text-[15px] font-bold text-ink">{provider.name} ({provider.id})</div>
        </div>
        <div>
          <div className="text-xs text-muted">Status</div>
          <div className="mt-1"><ToneBadge tone={provider.statusTone}>{provider.statusLabel}</ToneBadge></div>
        </div>
        <div>
          <div className="text-xs text-muted">Uptime (30d)</div>
          <div className="mt-1 text-[15px] font-bold tabular-nums text-ink">{provider.uptime}</div>
        </div>
        <div>
          <div className="text-xs text-muted">Error Rate</div>
          <div className="mt-1 text-[15px] font-bold tabular-nums text-ink">{provider.errorRate}</div>
        </div>
        <div>
          <div className="text-xs text-muted">Latency p50</div>
          <div className="mt-1 text-[15px] font-bold tabular-nums text-ink">{provider.latencyP50}</div>
        </div>
        <div>
          <div className="text-xs text-muted">Latency p99</div>
          <div className="mt-1 text-[15px] font-bold tabular-nums text-ink">{provider.latencyP99}</div>
        </div>
      </div>

      <SectionTitle>Capability Matrix</SectionTitle>
      <div className="mb-6 flex flex-wrap gap-2">
        {provider.capabilities.map((c) => (
          <span
            key={c.name}
            className={cn(
              "inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[13px] font-semibold",
              c.enabled ? "border-success-border bg-success-soft text-success" : "border-line bg-bg text-muted"
            )}
          >
            <span aria-hidden>{c.enabled ? "✓" : "✕"}</span>
            {c.name}
          </span>
        ))}
      </div>

      <SectionTitle>Last 10 Probe Results</SectionTitle>
      <div className="mb-6 flex flex-wrap gap-2">
        {provider.probes.map((probe) => (
          <span
            key={probe}
            className="rounded-full border border-success-border bg-success-soft px-3 py-1 text-[13px] font-semibold text-success"
          >
            {probe}
          </span>
        ))}
      </div>

      <a href="#" className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline">
        View {provider.name} status page ↗
      </a>
    </div>
  );
}

/* MON-01 · Chain Monitor */
export function ChainMonitor() {
  const chain = CHAINS[0];

  return (
    <div>
      <Breadcrumb items={[{ label: "Monitoring", href: "/monitoring" }, { label: chain.name }]} />

      <div className="mb-6 grid grid-cols-2 gap-5 rounded-lg border border-line bg-surface px-5 py-4 sm:grid-cols-3">
        <div>
          <div className="text-xs text-muted">Chain</div>
          <div className="mt-1 text-[15px] font-bold text-ink">{chain.name}</div>
        </div>
        <div>
          <div className="text-xs text-muted">Status</div>
          <div className="mt-1"><ToneBadge tone={chain.statusTone}>{chain.statusLabel}</ToneBadge></div>
        </div>
        <div>
          <div className="text-xs text-muted">Block Height</div>
          <div className="mt-1 font-mono text-[15px] font-bold tabular-nums text-ink">{chain.blockHeight}</div>
        </div>
        <div>
          <div className="text-xs text-muted">Last Observation</div>
          <div className="mt-1 text-[15px] font-bold tabular-nums text-ink">{chain.lastObservation}</div>
        </div>
        <div>
          <div className="text-xs text-muted">Finality Threshold</div>
          <div className="mt-1 text-[15px] font-bold text-ink">{chain.finality}</div>
        </div>
        <div>
          <div className="text-xs text-muted">Reorgs (24h)</div>
          <div className="mt-1 text-[15px] font-bold tabular-nums text-ink">{chain.reorgs}</div>
        </div>
      </div>

      <SectionTitle>Active Watches</SectionTitle>
      <TableWrapper className="mb-6">
        <Table className="dense">
          <thead>
            <tr className="border-b border-line bg-bg text-left text-xs font-semibold text-muted">
              <th className="px-4 py-2.5">Watch ID</th>
              <th className="px-4 py-2.5">Address</th>
              <th className="px-4 py-2.5">Type</th>
              <th className="px-4 py-2.5">Since</th>
              <th className="px-4 py-2.5">Last Event</th>
            </tr>
          </thead>
          <tbody>
            {chain.watches.map((w) => (
              <tr key={w.id} className="border-b border-line last:border-b-0">
                <td className="px-4 py-2.5 font-mono text-[13px] text-ink">{w.id}</td>
                <td className="px-4 py-2.5 font-mono text-[13px] text-text">{w.address}</td>
                <td className="px-4 py-2.5 text-[13px] text-text">{w.type}</td>
                <td className="px-4 py-2.5 text-[13px] text-muted">{w.since}</td>
                <td className="px-4 py-2.5 text-[13px] text-muted">{w.lastEvent}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </TableWrapper>

      <SectionTitle>Checkpoint History</SectionTitle>
      <EventTimeline
        events={chain.checkpoints.map((c) => ({
          title: c.desc,
          ts: c.ts,
          tone: "success" as const,
        }))}
      />
    </div>
  );
}

/* MON-03 · Broadcast Monitor */
export function BroadcastMonitor() {
  return (
    <div>
      <Breadcrumb items={[{ label: "Monitoring", href: "/monitoring" }, { label: "Broadcast Monitor" }]} />

      <div className="mb-5 grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-5">
        {BROADCAST_STATS.map((s) => (
          <StatCard key={s.label} value={s.value} label={s.label} />
        ))}
      </div>

      <div className="mb-6">
        <SegmentedBar segments={PIPELINE_BAR} height={10} />
        <HealthLegend segments={PIPELINE_BAR} />
      </div>

      <SectionTitle>Broadcast Queue</SectionTitle>
      <TableWrapper>
        <Table className="dense">
          <thead>
            <tr className="border-b border-line bg-bg text-left text-xs font-semibold text-muted">
              <th className="px-4 py-2.5">TX ID</th>
              <th className="px-4 py-2.5">Chain</th>
              <th className="px-4 py-2.5">Status</th>
              <th className="px-4 py-2.5">Submitted</th>
              <th className="px-4 py-2.5">Gas (Gwei)</th>
              <th className="px-4 py-2.5">Confirmations</th>
            </tr>
          </thead>
          <tbody>
            {BROADCAST_ROWS.map((r) => (
              <tr key={r.txId} className="border-b border-line last:border-b-0">
                <td className="px-4 py-3 font-mono text-[13px] font-semibold text-ink">{r.txId}</td>
                <td className="px-4 py-3 text-[13px] text-text">{r.chain}</td>
                <td className="px-4 py-3"><ToneBadge tone={r.statusTone}>{r.statusLabel}</ToneBadge></td>
                <td className="px-4 py-3 font-mono text-[13px] text-muted">{r.submitted}</td>
                <td className="px-4 py-3 font-mono text-[13px] tabular-nums text-text">{r.gas}</td>
                <td className="px-4 py-3 font-mono text-[13px] tabular-nums text-text">{r.confirmations}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </TableWrapper>
    </div>
  );
}

function FreshCell({ value }: { value: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-success">
      <span aria-hidden className="size-2 shrink-0 rounded-full bg-success" />
      {value}
    </span>
  );
}

/* MON-04 · Source Freshness Inspector */
export function FreshnessMonitor() {
  return (
    <div>
      <Breadcrumb items={[{ label: "Monitoring", href: "/monitoring" }, { label: "Source Freshness" }]} />

      <InfoBanner variant="info">
        All sources within freshness tolerance. Last full scan: 2026-09-15 10:44:00 UTC.
      </InfoBanner>

      <SectionTitle>Freshness Matrix — Wallet × Source</SectionTitle>
      <TableWrapper>
        <Table className="dense">
          <thead>
            <tr className="border-b border-line bg-bg text-left text-xs font-semibold text-muted">
              <th className="px-4 py-2.5">Wallet</th>
              <th className="px-4 py-2.5">Balance Source</th>
              <th className="px-4 py-2.5">TX Source</th>
              <th className="px-4 py-2.5">Event Source</th>
            </tr>
          </thead>
          <tbody>
            {FRESHNESS_ROWS.map((r) => (
              <tr key={r.wallet} className="border-b border-line last:border-b-0">
                <td className="px-4 py-3 font-mono text-[13px] font-semibold text-ink">{r.wallet}</td>
                <td className="px-4 py-3"><FreshCell value={r.balance} /></td>
                <td className="px-4 py-3"><FreshCell value={r.tx} /></td>
                <td className="px-4 py-3"><FreshCell value={r.event} /></td>
              </tr>
            ))}
          </tbody>
        </Table>
      </TableWrapper>
    </div>
  );
}

function DetailRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-6 px-5 py-3">
      <span className="text-muted">{label}</span>
      <span className="text-right font-medium text-ink">{children}</span>
    </div>
  );
}

/* MON-05 · Alerts & Incidents */
export function AlertsRegister() {
  const [overrides, setOverrides] = React.useState<Record<string, AlertStatus>>({});
  const rows = ALERTS.map((a) => ({ ...a, status: overrides[a.id] ?? a.status }));

  const acknowledge = (id: string) => {
    setOverrides((prev) => ({ ...prev, [id]: "Acknowledged" }));
    toast({ variant: "info", title: "Alert acknowledged", description: localOnly(id) });
  };

  return (
    <div>
      <Breadcrumb items={[{ label: "Monitoring", href: "/monitoring" }, { label: "Alerts & Incidents" }]} />

      <SectionTitle>All Alerts</SectionTitle>
      <TableWrapper className="mb-6">
        <Table className="dense">
          <thead>
            <tr className="border-b border-line bg-bg text-left text-xs font-semibold text-muted">
              <th className="px-4 py-2.5">ID</th>
              <th className="px-4 py-2.5">Title</th>
              <th className="px-4 py-2.5">Severity</th>
              <th className="px-4 py-2.5">Owner</th>
              <th className="px-4 py-2.5">Status</th>
              <th className="px-4 py-2.5">Created</th>
              <th className="px-4 py-2.5">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((a) => (
              <tr key={a.id} className="border-b border-line last:border-b-0">
                <td className="px-4 py-3 font-mono text-[13px] font-semibold text-ink">{a.id}</td>
                <td className="px-4 py-3 text-[13px] font-semibold text-ink">{a.title}</td>
                <td className="px-4 py-3"><ToneBadge tone={severityTone(a.severity)}>{a.severity}</ToneBadge></td>
                <td className="px-4 py-3 text-[13px] text-muted">{a.owner}</td>
                <td className="px-4 py-3"><ToneBadge tone={statusToneFor(a.status)}>{a.status}</ToneBadge></td>
                <td className="px-4 py-3 text-[13px] text-muted">{a.created}</td>
                <td className="px-4 py-3">
                  {a.action === "Acknowledge" && a.status === "Open" ? (
                    <button type="button" onClick={() => acknowledge(a.id)} className="text-[13px] font-medium text-primary hover:underline">
                      Acknowledge
                    </button>
                  ) : (
                    <a href="#alert-detail" className="text-[13px] font-medium text-primary hover:underline">
                      View
                    </a>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </TableWrapper>

      <SectionTitle>Alert Detail — {ALERT_DETAIL.id}</SectionTitle>
      <div id="alert-detail" />
      <Panel bodyClassName="p-0">
        <div className="border-b border-line bg-bg px-5 py-3 text-[13px] font-bold text-ink">
          {ALERT_DETAIL.id} · {ALERT_DETAIL.title}
        </div>
        <div className="divide-y divide-line text-[13px]">
          <DetailRow label="Title">{ALERT_DETAIL.title}</DetailRow>
          <DetailRow label="Severity">
            <ToneBadge tone={severityTone(ALERT_DETAIL.severity)}>{ALERT_DETAIL.severity}</ToneBadge>
          </DetailRow>
          <DetailRow label="Owner">{ALERT_DETAIL.owner}</DetailRow>
          <DetailRow label="Status">
            <ToneBadge tone={statusToneFor(ALERT_DETAIL.status)}>{ALERT_DETAIL.status}</ToneBadge>
          </DetailRow>
          <DetailRow label="Created">{ALERT_DETAIL.created}</DetailRow>
          <DetailRow label="Receipt">{ALERT_DETAIL.receipt}</DetailRow>
          <DetailRow label="Evidence">
            <Link to="/evidence" className="font-medium text-primary hover:underline">
              {ALERT_DETAIL.evidence}
            </Link>
          </DetailRow>
        </div>
        <div className="border-t border-line px-5 py-4">
          <EventTimeline
            events={ALERT_DETAIL.timeline.map((t) => ({
              title: t.title,
              ts: `${t.ts} UTC`,
              tone: "success" as const,
            }))}
          />
        </div>
      </Panel>
    </div>
  );
}
